import { useEffect } from 'react';
import useBrandSettings from './useBrandSettings';

/**
 * Applies the brand's admin-configured SEO settings (Brands → SEO tab,
 * `seo.title` / `seo.description` on GET /api/brands/{slug}) to the document
 * while a brand page is mounted; restores the previous title/meta on unmount
 * so other brands / the dev project picker keep their own.
 *
 * Empty settings are a no-op — the static index.html defaults stay.
 */
export default function useBrandSeo(brandSlug) {
  const { seo } = useBrandSettings(brandSlug);
  const title = seo?.title || '';
  const description = seo?.description || '';

  useEffect(() => {
    if (!title && !description) return undefined;

    const prevTitle = document.title;
    if (title) document.title = title;

    let meta = document.querySelector('meta[name="description"]');
    const created = !meta && !!description;
    const prevDescription = meta?.getAttribute('content') ?? null;
    if (description) {
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'description');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', description);
    }

    return () => {
      if (title) document.title = prevTitle;
      if (description && meta) {
        if (created) meta.remove();
        else if (prevDescription !== null) meta.setAttribute('content', prevDescription);
      }
    };
  }, [title, description]);
}
